import { MarkdownRenderer } from "components/markdown";
import { Container } from "components/ui/container";
import { necessary, necessaryCn } from "modules/cookie/config";
import { useTheme } from "@mui/material/styles";
import { Box } from "components/ui/box";
import { Typography } from "components/ui/Typography";
import { PageHeadTags } from "common/head-tags";
import { serverRoutesConfig } from "client-server-shared/config/routes";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { isCn, locale } from "client-server-shared/constants";
import { useTranslation } from "components/translate";

const firstSection = `
## What are cookies?

This Cookie Policy provides information on cookies, their usage, types, data collection, and management of cookie settings. Cookies are small text files stored on your device to enhance website functionality and user experience. 

## How do we use cookies?

Our website partners with a third-party advertising company, Adsterra, which utilizes cookies. The use of these cookies is primarily aimed at delivering personalized advertisements based on your browsing history. 

[Adsterra's cookies](https://help-publishers.adsterra.com/en/articles/6144870-cookies-policy) do not gather any personally identifiable information. They are used solely for the purpose of displaying relevant ads and enhancing your online experience.

Please note that our website does not use any other first-party or third-party cookies apart from those from Adsterra.

## Types of Cookies we use

The only type of cookies used on our website are third-party cookies from Adsterra. These cookies may be used to serve you advertisements based on your prior visits to our website or other websites.

---

Different browsers provide different methods to block and delete cookies used by websites. You can change the settings of your browser to block/delete the cookies. Listed below are the links to the support documents on how to manage and delete cookies from the major web browsers.

Chrome: [https://support.google.com/accounts/answer/32050](https://support.google.com/accounts/answer/32050)

Safari: [https://support.apple.com/en-in/guide/safari/sfri11471/mac](https://support.apple.com/en-in/guide/safari/sfri11471/mac)

Firefox: [https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?redirectslug=delete-cookies-remove-info-websites-stored&redirectlocale=en-US](https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?redirectslug=delete-cookies-remove-info-websites-stored&redirectlocale=en-US)

Internet Explorer: [https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc](https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc)

If you are using any other web browser, please visit your browser’s official support documents.
`;

const CookieSection = ({ title, options }) => {
  const { t } = useTranslation("cookie");

  return (
    <>
      <h3>{title}</h3>
      <table className="table" striped bordered hover>
        <thead>
          <tr>
            <th>{t("Cookie")}</th>
            <th>{t("Duration")}</th>
            <th>{t("Description")}</th>
          </tr>
        </thead>
        <tbody>
          {options.map((option, index) => {
            return (
              <tr key={index}>
                <td>{option.name}</td>
                <td>{option.duration}</td>
                <td>{option.detailDescription || option.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

const CookiePolicy = () => {
  const theme = useTheme();
  const { t } = useTranslation("cookie");

  return (
    <>
      <PageHeadTags
        pageLink={serverRoutesConfig.cookiePolicy}
        title={t("metaTitle")}
        description={t("metaDescription")}
      />
      <Container maxWidth="xl" marginTop={48} marginBottom={48}>
        <Box
          sx={{
            display: "flex",
          }}
          marginBottom={48}
        >
          <Typography
            variant="h1"
            sx={{
              paddingBottom: "16px",
              borderBottom: `3px solid ${theme.palette.primary.main}}`,
            }}
          >
            {t("Cookie Policy")}
          </Typography>
        </Box>
        <MarkdownRenderer className="markdown-v2" input={firstSection} />
      </Container>
    </>
  );
};

export default CookiePolicy;

export async function getStaticProps() {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["cookie", "common"])),
      // Will be passed to the page component as props
    },
  };
}
